import React, { forwardRef } from "react";
import { Form, Row, Col, Button } from 'react-bootstrap';
import BaseComponent from './BaseComponent'
import { IIncidentInfo, IViolationList, ViolationSource } from "../classes/Domain";
import { validationUtils } from "../validation/Validation";
import Select, { ValueType, ActionMeta } from "react-select"
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import MaskedTextInput from "react-input-mask";
import Utilities from "../classes/Utilities";
import { buildD2cViolationSelectOptions } from "../config/d2cViolationOptions";

type SelectOption = {
    value: string,
    label: string
}

type SelectGroupOption = {
    label: string,
    options: Array<SelectOption>
}

const today = new Date();

const CustomDateInput = forwardRef(function CustomDateInput(props: { className, value, onClick, onFocus, onChange }, ref) {
    return (
        <MaskedTextInput
            className={props.className}
            type="text"
            mask='99/99/9999'
            placeholder='mm/dd/yyyy'
            value={props.value}
            onFocus={props.onFocus}
            onChange={props.onChange}
            onClick={props.onClick}
            ref={ref}
        />
    );
});

function buildFullCatalogOptions(violations: IViolationList): Array<SelectGroupOption> {
    return (violations.violationGroups || []).map(x => ({
        label: x.groupName,
        options: (x.violations || []).map(v => ({ label: v.text, value: v.value }))
    }));
}

function resolveSelectedOption(
    options: Array<SelectGroupOption> | Array<SelectOption>,
    incident: IIncidentInfo,
    useD2cFlat: boolean
): SelectOption | null {
    if (!incident?.violationCode) {
        return null;
    }

    if (useD2cFlat) {
        const flat = options as Array<SelectOption>;
        return flat.find(o => o.value === incident.violationCode)
            || (incident.violation ? { label: incident.violation, value: incident.violationCode } : null);
    }

    for (const group of options as Array<SelectGroupOption>) {
        const match = (group.options || []).find(o => o.value === incident.violationCode);
        if (match) {
            return match;
        }
    }

    return incident.violation
        ? { label: incident.violation, value: incident.violationCode }
        : null;
}

interface IState {
    category: string,
    violation: string,
    violationCode: string,
    violationDate: Date,
    convictionDate: Date | null,
    options: Array<SelectGroupOption> | Array<SelectOption>,
    selectedOption: SelectOption | null,
    useD2cFlat: boolean
}

interface IProps {
    updatingStore?: boolean,
    index: number,
    incident: IIncidentInfo,
    violations: IViolationList,
    /** Qualitas quick path: CA/TX → aggregated D2C labels (phase 3.2). */
    stateCode?: string,
    errors: any,
    onChange(incident: IIncidentInfo, index: number): void,
    onDelete(index: number): void
}


class IncidentInfo extends BaseComponent<IProps, IState> {
    violationDateRef: React.RefObject<MaskedTextInput>;
    convictionDateRef: React.RefObject<MaskedTextInput>;
	constructor(props: IProps) {
        super(props);
        this.violationDateRef = React.createRef();
        this.convictionDateRef = React.createRef();

        const d2cOptions = buildD2cViolationSelectOptions(props.stateCode, props.violations);
        const useD2cFlat = !!(d2cOptions && d2cOptions.length > 0);
        const options = useD2cFlat ? d2cOptions! : buildFullCatalogOptions(props.violations);

        this.state = {
            category: this.props.incident.category,
            violation: this.props.incident.violation,
            violationCode: this.props.incident.violationCode,
            violationDate: this.props.incident.violationDate,
            convictionDate: this.props.incident.convictionDate,
            options,
            useD2cFlat,
            selectedOption: resolveSelectedOption(options, this.props.incident, useD2cFlat)
		};

        this.onIncidentChange = this.onIncidentChange.bind(this);
        this.onIncidentViolationDateChange = this.onIncidentViolationDateChange.bind(this);
        this.onIncidentViolationDateChangeRaw = this.onIncidentViolationDateChangeRaw.bind(this);
        this.onIncidentConvictionDateChange = this.onIncidentConvictionDateChange.bind(this);
        this.onIncidentConvictionDateChangeRaw = this.onIncidentConvictionDateChangeRaw.bind(this);
        this.onIncidentDelete = this.onIncidentDelete.bind(this);
        this.getIncident = this.getIncident.bind(this);
    }

    private getIncident(): IIncidentInfo {
        return {
            category: this.state.category,
            violation: this.state.violation,
            violationCode: this.state.violationCode,
            violationDate: this.state.violationDate,
            convictionDate: this.state.convictionDate,
            incidentDescription: "",
            source: ViolationSource.ClientOrSystem
        };
    }

    onIncidentChange(newValues: ValueType<any>, action: ActionMeta<SelectOption>) {
        if (!newValues) {
            return;
        }

        let category = "";
        if (this.state.useD2cFlat) {
            // Aggregated D2C label is display-only; code is always catalog value.
            category = newValues.label;
        } else {
            category = this.props.violations.violationGroups.find(x => x.violations.find(v => v.value === newValues.value))?.groupName ?? "";
        }

        this.setState({
            category,
            violation: newValues.label,
            violationCode: newValues.value,
            selectedOption: newValues
        }, () => this.props.onChange(this.getIncident(), this.props.index));
    }

    onIncidentViolationDateChange(value: Date) {
        let convictionDate = this.state.convictionDate;

        if (convictionDate && convictionDate < value) {
            convictionDate = null;
        }

        this.setState({ violationDate: value, convictionDate: convictionDate }, () => this.props.onChange(this.getIncident(), this.props.index));
    }

    onIncidentViolationDateChangeRaw = (date) => {
        const newRaw = Utilities.parseDate(date.currentTarget.value);
        if (newRaw) {
            this.onIncidentViolationDateChange(newRaw);
        }
    };

    onIncidentConvictionDateChange(value: Date) {
        if (value) {
            let violationDate = this.state.violationDate;
            violationDate.setHours(0);
            violationDate.setMinutes(0);
            violationDate.setSeconds(0);
            violationDate.setMilliseconds(0);
            if (value >= violationDate && value <= today) {
                this.setState({ convictionDate: value }, () => this.props.onChange(this.getIncident(), this.props.index));
            }
        }
    }

    onIncidentConvictionDateChangeRaw = (date) => {
        const newRaw = Utilities.parseDate(date.currentTarget.value);
        if (newRaw) {
            this.onIncidentConvictionDateChange(newRaw);
        }
    };

    onIncidentDelete() {
        if (!this.props.updatingStore) {
            this.props.onDelete(this.props.index);
        }
    }

    getIncidentYears() {
        let years = [] as Array<number>;
        const currentYear = new Date().getFullYear();
        for (let i = currentYear; i >= currentYear - 3; i--) {
            years.push(i);
        }
        return years;
    }

	render() {
        return (
            <div className="vehicle-row">
                <Row>
                    <Col sm={8}>
                        <Select
                            options={this.state.options}
                            value={this.state.selectedOption}
                            placeholder={"Please Select..."}
                            onChange={this.onIncidentChange}
                            autoFocus={false}
                            isClearable={false}
                            isDisabled={this.props.updatingStore}
                        />
                        {
                            null != this.props.errors && validationUtils.hasValidationErrorFor(this.props.errors, "incidents.violationCode") && this.props.incident.violationCode === ""
                                ? <Form.Control.Feedback type="invalid">
                                    {validationUtils.getValidationErrorFor(this.props.errors, "incidents.violationCode")}
                                </Form.Control.Feedback>
                                : null
                        }
                    </Col>
                    <Col sm={3}>
                        <DatePicker
                            key={"violationDateDatePicker"}
                            className={"form-control"}
                            customInput={<CustomDateInput ref={this.violationDateRef} className={"form-control"} />}
                            showMonthDropdown={true}
                            showYearDropdown={true}
                            minDate={moment(today).add(-3, 'y').toDate()}
                            maxDate={moment(today).toDate()}
                            onChangeRaw={(e) => this.onIncidentViolationDateChangeRaw(e)}
                            onChange={this.onIncidentViolationDateChange}
                            selected={moment(this.state.violationDate).toDate()}
                            disabled={this.props.updatingStore} />
                    </Col>
                    <Col sm={1}>
                        <Button
                            className="btn-remove"
                            onClick={this.onIncidentDelete}
                            disabled={this.props.updatingStore}
                            title="Remove incident"
                            aria-label="Remove incident">
                            <i className="fas fa-trash"></i>
                        </Button>
                    </Col>
                </Row>
            </div>
		);
	}
}

export default IncidentInfo;
